import { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import {
  LoadingOverlay,
  MOBILE_BREAKPOINT,
  PillSwitchButtons,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from '@sunrun/experience-ui-components';
import { isRetailUser } from '@sunrun/sales-experience-shared';
import { useFeatureFlagEnabled } from '@sunrun/sales-experience-shared/react-feature-flags';
import { UnderlinedTabGroup } from 'components/molecules/UnderlinedTabGroup';
import { DashboardWidgetContainer } from 'components/atoms/DashboardWidgetContainer';
import { AnalyticsIFrame } from 'components/organisms/AnalyticsIFrame';
import { CoachingIFrame } from 'components/organisms/CoachingIFrame';
import { HeliosIFrame } from 'components/organisms/HeliosIFrame';
import { LeaderboardWidget } from 'components/organisms/LeaderboardWidget';
import { PerformanceWidget } from 'components/organisms/PerformanceWidget';
import { FEATURE_FLAGS } from 'constants/FeatureFlags';
import {
  NewDashboardOrgType,
  getNewDashboardOrgType,
} from 'helpers/repTypeHelper';
import { useUnreadCoachingCount } from 'hooks/useUnreadCoachingCount';
import { getChatUserRoleFromSplatUserType } from 'lib/chat-unread.utils';
import { UserRoleType } from 'lib/db-interfaces';
import { publishMessage } from 'helpers/messaging';
import { useHybridToken } from 'providers/Auth';
import { useUserProfile } from 'providers/UserProfile';

const PERFORMANCE_COACHING_PATH =
  /^\/performance\/coaching(?:\/([^/?#]+))?\/?$/;
const PERFORMANCE_HELIOS_PATH = /^\/performance\/helios(?:\/([^/?#]+))?\/?$/;

const parseCoachingPath = (pathname: string) => {
  const match = pathname.match(PERFORMANCE_COACHING_PATH);
  if (!match) {
    return null;
  }
  return { sessionId: match[1] ?? undefined };
};

const parseHeliosPath = (pathname: string) => {
  const match = pathname.match(PERFORMANCE_HELIOS_PATH);
  if (!match) {
    return null;
  }
  return { sessionId: match[1] ?? undefined };
};

type PerformanceViewMode = 'new' | 'classic';
type PerformancePageTab = 'performance' | 'coaching' | 'helios';
type NewViewMobileTab = 'performance' | 'leaderboard';

const HEADER_COLLAPSE_THRESHOLD = 20;
const THE_BIGS_URLS = {
  BLOCK: 'https://blockbigs.netlify.app/',
  STREET: 'https://streetbigs.netlify.app/',
} as const;

const getTheBigsUrl = (orgType: NewDashboardOrgType): string | undefined => {
  switch (orgType) {
    case 'BLOCK':
      return THE_BIGS_URLS.BLOCK;
    case 'STREET':
      return THE_BIGS_URLS.STREET;
    case 'CHAIN':
    case 'AIR':
    case 'UNKNOWN':
      return undefined;
    default: {
      const exhaustiveCheck: never = orgType;
      return exhaustiveCheck;
    }
  }
};

type PerformancePageTabsProps = {
  pageTab: PerformancePageTab;
  onPageTabChange: (tab: PerformancePageTab) => void;
  repContactId: string | null | undefined;
  repRole: UserRoleType | null | undefined;
  coachingEnabled: boolean;
  heliosEnabled: boolean;
};

const PerformancePageTabs = ({
  pageTab,
  onPageTabChange,
  repContactId,
  repRole,
  coachingEnabled,
  heliosEnabled,
}: PerformancePageTabsProps) => {
  const { coachingUnreadCount } = useUnreadCoachingCount(repContactId, repRole);

  const options = [
    { label: 'Performance', value: 'performance' as const },
    ...(coachingEnabled
      ? [
          {
            label: 'Coaching',
            value: 'coaching' as const,
            badgeCount: coachingUnreadCount,
          },
        ]
      : []),
    ...(heliosEnabled ? [{ label: 'Helios', value: 'helios' as const }] : []),
  ];

  return (
    <UnderlinedTabGroup
      aria-label="Performance page tabs"
      options={options}
      value={pageTab}
      onChange={onPageTabChange}
      size="lg"
      justify="flex-start"
    />
  );
};

const Performance = () => {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { userProfile, splatUserType } = useUserProfile();
  const { hybridToken } = useHybridToken();
  const orgType = getNewDashboardOrgType(userProfile);
  const [viewMode, setViewMode] = useState<PerformanceViewMode>('new');
  const [mobileTab, setMobileTab] = useState<NewViewMobileTab>('performance');
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: MOBILE_BREAKPOINT });
  const coachingEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.COACHING_ENABLED);
  const heliosEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.HELIOS_V0);
  const newLeaderboardEnabled = useFeatureFlagEnabled(
    FEATURE_FLAGS.NEW_LEADERBOARD_ENABLED,
  );
  const theBigsLinkEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.THE_BIGS_LINK);
  const theBigsUrl = getTheBigsUrl(orgType);
  const showTheBigsLink = Boolean(theBigsUrl) && theBigsLinkEnabled;
  const isRetail = Boolean(splatUserType && isRetailUser(splatUserType));
  const lockPerformanceToClassic =
    Boolean(coachingEnabled) && isRetail && !newLeaderboardEnabled;

  const coachingRoute = parseCoachingPath(pathname);
  const heliosRoute = parseHeliosPath(pathname);

  // Falls back to 'performance' when the flag is off, so a stale deep link
  // to /performance/coaching or /performance/helios doesn't leave the rep on an empty pane.
  const pageTab: PerformancePageTab =
    heliosRoute !== null && Boolean(heliosEnabled)
      ? 'helios'
      : coachingRoute !== null && Boolean(coachingEnabled)
      ? 'coaching'
      : 'performance';
  const coachingSessionId = coachingRoute?.sessionId;
  const heliosSessionId = heliosRoute?.sessionId;

  const showViewModeToggle =
    Boolean(userProfile) && orgType !== 'BLOCK' && !lockPerformanceToClassic;
  const showViewModeToggleOnHeader =
    pageTab === 'performance' && showViewModeToggle;
  const showHeaderActions = showTheBigsLink || showViewModeToggleOnHeader;
  const effectiveViewMode = lockPerformanceToClassic
    ? 'classic'
    : showViewModeToggle
    ? viewMode
    : 'new';
  const showCoachingTab = Boolean(coachingEnabled) && pageTab === 'coaching';
  const showHeliosTab = Boolean(heliosEnabled) && pageTab === 'helios';
  const showPerformancePageTabs =
    Boolean(coachingEnabled) || Boolean(heliosEnabled);
  const repContactId = userProfile?.contactId;
  const repRole = getChatUserRoleFromSplatUserType(splatUserType) ?? null;

  const jwt = searchParams.get('jwt');
  const email = searchParams.get('email');

  const isLeaderboardMobileTab =
    isMobile && effectiveViewMode === 'new' && mobileTab === 'leaderboard';

  useEffect(() => {
    if (!isLeaderboardMobileTab) {
      setIsHeaderCollapsed(false);
    }
  }, [isLeaderboardMobileTab]);

  const handlePageTabChange = useCallback(
    (tab: PerformancePageTab) => {
      if (tab === pageTab) {
        return;
      }
      const path =
        tab === 'coaching'
          ? '/performance/coaching'
          : tab === 'helios'
          ? '/performance/helios'
          : '/performance';
      const search = searchParams.toString();
      navigate(
        {
          pathname: path,
          search: search ? `?${search}` : '',
        },
        { replace: true },
      );
      publishMessage({
        source: 'Dashboard',
        type: 'COMMAND:CHANGE_URL',
        payload: { url: path },
      });
    },
    [navigate, pageTab, searchParams],
  );

  const handleLeaderboardScroll = useCallback(
    (scrollTop: number) => {
      if (isLeaderboardMobileTab) {
        const isCollapsed = scrollTop > HEADER_COLLAPSE_THRESHOLD;
        setIsHeaderCollapsed((prev) =>
          prev === isCollapsed ? prev : isCollapsed,
        );
      }
    },
    [isLeaderboardMobileTab],
  );

  const renderNewViewContent = () => {
    if (isMobile) {
      return (
        <>
          {!isHeaderCollapsed && (
            <MobileTabRow>
              <UnderlinedTabGroup
                aria-label="Performance page sections"
                options={[
                  { label: 'SCORECARD', value: 'performance' },
                  { label: 'LEADERBOARD', value: 'leaderboard' },
                ]}
                value={mobileTab}
                onChange={setMobileTab}
              />
            </MobileTabRow>
          )}
          <MobileContent>{renderMobileTabContent()}</MobileContent>
        </>
      );
    }

    return (
      <ColumnsRow>
        <LeftColumn>
          <DashboardWidgetContainer header="Scorecard">
            <PerformanceWidget orgType={orgType} />
          </DashboardWidgetContainer>
        </LeftColumn>
        <RightColumn>
          <LeaderboardWidget />
        </RightColumn>
      </ColumnsRow>
    );
  };

  const renderMobileTabContent = () => {
    switch (mobileTab) {
      case 'leaderboard':
        return <LeaderboardWidget onTableScroll={handleLeaderboardScroll} />;
      default: {
        return <PerformanceWidget orgType={orgType} />;
      }
    }
  };

  const renderCoachingContent = () => {
    if (!hybridToken) {
      return <LoadingOverlay message="Loading..." />;
    }

    return (
      <ClassicViewContainer>
        <CoachingIFrame sessionId={coachingSessionId} />
      </ClassicViewContainer>
    );
  };

  const renderHeliosContent = () => {
    if (!hybridToken) {
      return <LoadingOverlay message="Loading..." />;
    }

    return (
      <ClassicViewContainer>
        <HeliosIFrame sessionId={heliosSessionId} />
      </ClassicViewContainer>
    );
  };

  const renderContent = () => {
    switch (effectiveViewMode) {
      case 'classic':
        if (!jwt || !email) {
          console.error('No jwt or email in performance page');
          return <LoadingOverlay message="Loading..." />;
        }
        return (
          <ClassicViewContainer>
            <AnalyticsIFrame jwt={jwt} email={email} />
          </ClassicViewContainer>
        );
      default: {
        return renderNewViewContent();
      }
    }
  };

  return (
    <Container>
      <PageHeader $collapsed={isHeaderCollapsed && isLeaderboardMobileTab}>
        {showPerformancePageTabs ? (
          <PerformancePageTabs
            pageTab={pageTab}
            onPageTabChange={handlePageTabChange}
            repContactId={repContactId}
            repRole={repRole}
            coachingEnabled={Boolean(coachingEnabled)}
            heliosEnabled={Boolean(heliosEnabled)}
          />
        ) : (
          <Typography variant="h2">Performance</Typography>
        )}
        {showHeaderActions && (
          <HeaderActions>
            {showTheBigsLink && (
              <TheBigsLinkButton
                type="button"
                data-testid="the-bigs-link"
                aria-label="The Bigs"
                onClick={() => {
                  window.open(theBigsUrl, '_blank');
                }}>
                <SvgIcon
                  name={SvgNames.ArrowSquareOutSvg}
                  width={14}
                  height={14}
                  color={tokens.BRAND_HEROBLUE}
                />
                <TheBigsLinkLabel variant="span" size="small">
                  The Bigs
                </TheBigsLinkLabel>
              </TheBigsLinkButton>
            )}
            {showViewModeToggleOnHeader && (
              <PillSwitchButtons
                aria-label="Performance view mode"
                options={[
                  { label: 'New', value: 'new' },
                  { label: 'Classic', value: 'classic' },
                ]}
                value={viewMode}
                onChange={setViewMode}
              />
            )}
          </HeaderActions>
        )}
      </PageHeader>
      {showCoachingTab
        ? renderCoachingContent()
        : showHeliosTab
        ? renderHeliosContent()
        : renderContent()}
    </Container>
  );
};

const Container = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100vh;
  box-sizing: border-box;
`;

const PageHeader = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: ${({ $collapsed }) => ($collapsed ? 0 : '24px 0')};
  transition: padding 0.3s ease;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 16px;
  margin-left: auto;
`;

const TheBigsLinkButton = styled.button`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 4px 12px;
  border: 1px solid ${tokens.BRAND_HEROBLUE};
  border-radius: 9999px;
  background: transparent;
  cursor: pointer;
  font: inherit;
  color: ${tokens.BRAND_HEROBLUE};
  white-space: nowrap;

  &:hover {
    background-color: rgba(31, 38, 71, 0.06);
  }

  &:focus-visible {
    outline: 2px solid ${tokens.BRAND_HEROBLUE};
    outline-offset: 2px;
  }
`;

const TheBigsLinkLabel = styled(Typography)`
  color: ${tokens.BRAND_HEROBLUE};
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.24px;
  line-height: 1.2;
`;

const MobileTabRow = styled.div`
  padding: 0 16px;
`;

const MobileContent = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  min-width: 0;
  width: 100%;
  justify-content: center;
`;

const ColumnsRow = styled.div`
  display: flex;
  gap: 16px;
  flex: 1;
  min-height: 0;
`;

const LeftColumn = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const RightColumn = styled.div`
  flex: 2;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const ClassicViewContainer = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

export { Performance };
